// src/components/cards/AudioCard.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Text,
  HStack,
  VStack,
  IconButton,
  Divider,
  Link,
  useColorModeValue,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import {
  ShareIcon,
  FilePdfIcon,
  BookmarkSimpleIcon,
  StopIcon,
  PlayIcon,
  YoutubeLogoIcon,
  Icon,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { extractApiErrorMessage } from "../../helpers/utils";
import { triggerOnboardingOnce } from "../../helpers/onboarding";
import {
  buildAudioCardTourSteps,
  runOnboardingTour,
} from "../../helpers/onboardingTour";

type AudioCardProps = {
  recordingID: string;
  title: string;
  preacherName?: string;
  date?: string;
  summaryUrl?: string;
  recordingUrl?: string;
  hasSummary?: boolean;
  hasRecording?: boolean;
  youtubeLink?: string;
  saved?: boolean;
  onAddFavorite?: (recordingID: string) => Promise<void> | void;
  onRemoveFavorite?: (recordingID: string) => Promise<void> | void;
  onToggleFavorite?: (
    recordingID: string,
    nextValue: boolean,
  ) => Promise<void> | void;
  onRequestFiles: (recordingID: string) => Promise<{
    recordingUrl?: string;
    summaryUrl?: string;
  }>;
  isSearchResult?: boolean;
  sharePath?: string;
};

type AudioAction = {
  key: "save" | "share" | "summary";
  label: string;
  icon: Icon;
  onClick?: () => Promise<void> | void;
  isFilled?: boolean;
  isBusy?: boolean;
};

const AudioCard: React.FC<AudioCardProps> = ({
  recordingID,
  title,
  preacherName,
  date,
  summaryUrl,
  recordingUrl,
  hasSummary = false,
  hasRecording = false,
  youtubeLink,
  saved = false,
  onAddFavorite,
  onRemoveFavorite,
  onToggleFavorite,
  onRequestFiles,
  sharePath,
}) => {
  const toast = useToast();
  const location = useLocation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFavorite, setIsFavorite] = useState(saved);
  const [isSavingFavorite, setIsSavingFavorite] = useState(false);
  const [isFetchingFiles, setIsFetchingFiles] = useState(false);
  const [resolvedFiles, setResolvedFiles] = useState<{
    recordingUrl?: string;
    summaryUrl?: string;
  }>({
    recordingUrl,
    summaryUrl,
  });
  const audioRef = useRef<HTMLAudioElement>(null);
  const gray = useColorModeValue("gray.500", "gray.400");
  const borderGray = useColorModeValue("#E2E8F0", "#51555E");
  const youtubeColor = useColorModeValue("#FF0000", "#FF5555");
  const { t } = useTranslation();

  useEffect(() => {
    setIsFavorite(saved);
  }, [saved]);

  useEffect(() => {
    setResolvedFiles({ recordingUrl, summaryUrl });
  }, [recordingUrl, summaryUrl]);

  // The audio card renders on many different pages (teachings, hymns,
  // exhortations, etc.) but its onboarding tour should only ever play once.
  useEffect(() => {
    triggerOnboardingOnce("audioCard", () =>
      runOnboardingTour(
        buildAudioCardTourSteps(t),
        t,
        undefined,
        "audio-card-onboarding",
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ensureFilesLoaded = async (): Promise<{
    recordingUrl?: string;
    summaryUrl?: string;
  }> => {
    if (resolvedFiles.recordingUrl || resolvedFiles.summaryUrl) {
      return resolvedFiles;
    }

    setIsFetchingFiles(true);
    try {
      const fetched = await onRequestFiles(recordingID);
      setResolvedFiles((prev) => ({ ...prev, ...fetched }));
      return { ...resolvedFiles, ...fetched };
    } finally {
      setIsFetchingFiles(false);
    }
  };

  const handlePlay = async () => {
    if (!hasRecording && !resolvedFiles.recordingUrl) return;

    const files = await ensureFilesLoaded();
    if (!files.recordingUrl) return;

    setIsPlaying(true);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.src = files.recordingUrl as string;
        audioRef.current.play();
      }
    }, 100);
  };

  const handleStop = () => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  const shareAudio = (title: string, recordingIDToShare: string) => {
    const basePath = sharePath ?? location.pathname;
    const url = `${window.location.origin}${basePath}?q=${encodeURIComponent(recordingIDToShare)}`;
    if (navigator.share) {
      navigator.share({ title, url });
    } else {
      alert(t("audio.shareUnavailable"));
    }
  };

  const handleToggleFavorite = async () => {
    const nextValue = !isFavorite;
    setIsSavingFavorite(true);
    try {
      if (nextValue) {
        if (onAddFavorite) {
          await onAddFavorite(recordingID);
        } else if (onToggleFavorite) {
          await onToggleFavorite(recordingID, nextValue);
        }
      } else if (onRemoveFavorite) {
        await onRemoveFavorite(recordingID);
      } else if (onToggleFavorite) {
        await onToggleFavorite(recordingID, nextValue);
      }

      setIsFavorite(nextValue);

      toast({
        title: nextValue
          ? t("pages.favorites.addedTitle")
          : t("pages.favorites.removedTitle"),
        description: nextValue
          ? t("pages.favorites.addedDescription")
          : t("pages.favorites.removedDescription"),
        status: "success",
        duration: 1500,
        isClosable: true,
        position: "top",
      });
    } catch (error) {
      toast({
        title: t("pages.favorites.actionFailedTitle"),
        description: extractApiErrorMessage(
          error,
          String(t("common.tryAgain")),
        ),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsSavingFavorite(false);
    }
  };

  const handleSummaryOpen = async () => {
    if (!hasSummary && !resolvedFiles.summaryUrl) return;

    const files = await ensureFilesLoaded();
    if (!files.summaryUrl) return;
    window.open(files.summaryUrl, "_blank", "noopener,noreferrer");
  };

  // Define bottom actions
  const actions: AudioAction[] = [
    {
      key: "save",
      label: isFavorite ? t("audio.saved") : t("audio.save"),
      icon: BookmarkSimpleIcon,
      isFilled: isFavorite,
      isBusy: isSavingFavorite,
      onClick: handleToggleFavorite,
    },
    {
      key: "share",
      label: t("audio.share"),
      icon: ShareIcon,
      onClick: () => shareAudio(title, recordingID),
    },
  ];

  if (hasSummary || summaryUrl) {
    actions.push({
      key: "summary",
      label: t("audio.summary"),
      icon: FilePdfIcon,
      isBusy: isFetchingFiles,
      onClick: handleSummaryOpen,
    });
  }

  const mainButton = youtubeLink ? (
    <Link id="onboarding-audio-main" href={youtubeLink} isExternal>
      <YoutubeLogoIcon size={40} weight="fill" color={youtubeColor} />
    </Link>
  ) : (
    <Box
      id="onboarding-audio-main"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {isFetchingFiles && (
        <Spinner
          position="absolute"
          size="xl"
          color="gray.400"
          thickness="1px"
        />
      )}
      <IconButton
        aria-label={isPlaying ? t("audio.stop") : t("audio.play")}
        icon={
          isPlaying ? (
            <StopIcon size={22} weight="fill" />
          ) : (
            <PlayIcon size={22} weight="fill" />
          )
        }
        onClick={isPlaying ? handleStop : handlePlay}
        borderRadius="full"
        colorScheme={isPlaying ? "red" : "gray"}
        isDisabled={isFetchingFiles || (!hasRecording && !recordingUrl)}
      />
    </Box>
  );

  return (
    <Box
      borderWidth="1px"
      borderRadius="xl"
      pt={4}
      bg="white"
      boxShadow="sm"
      w="full"
      _dark={{ bg: "gray.800" }}
      transition="0.3s ease"
    >
      <VStack align="start" spacing={3} w="full" h="full">
        {/* Top Row */}
        <HStack w="full" px={4} spacing={4} align="flex-start">
          {mainButton}

          <VStack align="start" spacing={0}>
            <Text fontWeight="bold" fontSize="md">
              {title}
            </Text>
            <Text fontSize="sm" color={gray}>
              {preacherName ?? t("audio.unknownSpeaker")}
            </Text>
            {date && (
              <Text fontSize="sm" color={gray} textTransform="capitalize">
                {date}
              </Text>
            )}
          </VStack>
        </HStack>

        {/* Audio Player (only when no YouTube link) */}
        {!youtubeLink && resolvedFiles.recordingUrl && isPlaying && (
          <Box w="full" px={4}>
            <audio
              ref={audioRef}
              src={resolvedFiles.recordingUrl}
              controls
              style={{ width: "100%" }}
            />
          </Box>
        )}

        {/* {!youtubeLink && isFetchingFiles && (
          <HStack px={4} w="full" spacing={2}>
            <Spinner size="sm" />
            <Text fontSize="sm" color={gray}>
              Loading recording files...
            </Text>
          </HStack>
        )} */}

        {/* Bottom Actions */}
        <HStack
          w="full"
          justify="space-evenly"
          borderTop="2px"
          borderColor={borderGray}
          h="full"
          alignItems="center"
          spacing={{ base: 2, md: 4 }}
          px={{ base: 2, md: 4 }}
        >
          {actions.map((action, idx) => (
            <React.Fragment key={action.label}>
              {idx > 0 && (
                <Divider
                  orientation="vertical"
                  borderColor={borderGray}
                  borderWidth="1px"
                  h="40px"
                  my={0}
                  py={0}
                />
              )}
              <HStack
                id={`onboarding-audio-${action.key}`}
                spacing={2}
                h="full"
                cursor={action.isBusy ? "not-allowed" : "pointer"}
                py={2}
                px={1}
                flex="1 1 0"
                minW="0"
                onClick={() => {
                  if (!action.isBusy) {
                    action.onClick?.();
                  }
                }}
                justifyContent="center"
                opacity={action.isBusy ? 0.6 : 1}
              >
                <Box
                  position="relative"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minW={{ base: "18px", md: "24px" }}
                  minH={{ base: "18px", md: "24px" }}
                >
                  {action.isBusy && (
                    <Spinner
                      position="absolute"
                      size={{ base: "md", md: "sm" }}
                      color="gray.400"
                      thickness="2px"
                    />
                  )}
                  <action.icon
                    size="24px"
                    weight={action.isFilled ? "fill" : "regular"}
                  />
                </Box>
                <Text
                  display="block"
                  fontSize={{ base: "12px", md: "14px", lg: "16px" }}
                  m={0}
                  noOfLines={1}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  maxW={{ base: "68px", sm: "90px", md: "unset" }}
                  textAlign="center"
                >
                  {action.label}
                </Text>
              </HStack>
            </React.Fragment>
          ))}
        </HStack>
      </VStack>
    </Box>
  );
};

export default AudioCard;
